import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import FilterToolbar from './components/FilterToolbar';
import NewsGrid from './components/NewsGrid';
import NewsStats from './components/NewsStats';
import Icon from '../../components/AppIcon';
import { supabase } from '../../utils/supabaseClient';
import { useVisitorTracking } from '../../hooks/useVisitorTracking';

const NewsAndAnnouncements = () => {
  // Initialize visitor tracking
  useVisitorTracking();

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedArticles, setExpandedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: 'all',
    sortBy: 'newest',
    priority: 'all'
  });
  const [activeTab, setActiveTab] = useState('media');
  const params = useParams();
  const navigate = useNavigate();

  // Local state for official docs sub-views
  const [officialMode, setOfficialMode] = useState('landing'); // 'landing' | 'list' | 'detail'
  const [officialCategory, setOfficialCategory] = useState('orders');
  const [officialSearch, setOfficialSearch] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);

  // อ่านค่าแฮชจาก URL เพื่อสลับแท็บให้ตรงกับลิงก์ซับเมนู
  useEffect(() => {
    const tab = params?.tab;
    if (tab && ['media','pr','official-docs','procurement'].includes(tab)) {
      setActiveTab(tab);
    } else if (!tab) {
      setActiveTab('media');
    }
  }, [params?.tab]);

  // อัปเดตแฮชและเลื่อนขึ้นบนเมื่อเปลี่ยนแท็บ เพื่อให้กดซับเมนูแล้วไปยังหน้านั้นทันที
  useEffect(() => {
    if (!params?.tab || params.tab !== activeTab) {
      navigate(`/news-and-announcements/${activeTab}`, { replace: true });
    }
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
  }, [activeTab]);

  // State for news articles
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState(null);

  // State for official documents
  const [officialDocs, setOfficialDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [docsError, setDocsError] = useState(null);

  // State for procurements (จัดซื้อจัดจ้าง)
  const [procurements, setProcurements] = useState([]);
  const [procLoading, setProcLoading] = useState(true);
  const [procError, setProcError] = useState(null);
  const [procCategory, setProcCategory] = useState('procure'); // procure | plan | contract
  const [procSearch, setProcSearch] = useState('');
  const [procFrom, setProcFrom] = useState('');
  const [procTo, setProcTo] = useState('');
  const [procMode, setProcMode] = useState('list'); // list | detail
  const [selectedProc, setSelectedProc] = useState(null);

  // Mock news data (truncated for brevity)
  // const mockArticles = [
  //   {
  //     id: 1,
  //     title: "ประกาศเปิดรับสมัครงานราชการ ตำแหน่งนักวิชาการพัฒนาชุมชน",
  //     summary: "สำนักงานเขตเปิดรับสมัครบุคคลเพื่อบรรจุและแต่งตั้งข้าราชการ ตำแหน่งนักวิชาการพัฒนาชุมชน ระดับปฏิบัติการ จำนวน 2 อัตรา",
  //     content: `สำนักงานเขตมีความประสงค์จะรับสมัครบุคคลเพื่อสอบแข่งขันเป็นข้าราชการ ในตำแหน่งนักวิชาการพัฒนาชุมชน ระดับปฏิบัติการ จำนวน 2 อัตรา\n\nคุณสมบัติผู้สมัคร:\n- มีสัญชาติไทย\n- อายุไม่เกิน 35 ปี นับถึงวันปิดรับสมัคร\n- วุฒิการศึกษาระดับปริญญาตรี สาขาพัฒนาชุมชน สังคมศาสตร์ หรือสาขาที่เกี่ยวข้อง\n- ไม่เป็นโรคติดต่อร้ายแรง โรคจิต หรือโรคที่เป็นอุปสรรคต่อการปฏิบัติหน้าที่\n\nการสมัคร:\n- ยื่นใบสมัครด้วยตนเองที่สำนักงานเขต ชั้น 2 ห้องทรัพยากรบุคคล\n- ระยะเวลารับสมัคร: 15-25 กันยายน 2567\n- เวลา: 08:30-16:30 น. (ไม่รับสมัครเสาร์-อาทิตย์)\n\nเอกสารประกอบการสมัคร:\n- ใบสมัคร (รับได้ที่สำนักงานเขตหรือดาวน์โหลดจากเว็บไซต์)\n- สำเนาบัตรประจำตัวประชาชน\n- สำเนาทะเบียนบ้าน\n- สำเนาวุฒิการศึกษา\n- รูปถ่าย 1 นิ้ว จำนวน 2 ใบ\n\nสอบถามรายละเอียดเพิ่มเติมได้ที่ หมายเลขโทรศัพท์ 02-123-4567 ต่อ 205`,
  //     category: 'ประกาศ',
  //     priority: 'สูง',
  //     publishDate: '2024-09-08',
  //     author: 'ฝ่ายทรัพยากรบุคคล',
  //     views: 2847,
  //     tags: ['สมัครงาน', 'ราชการ', 'นักวิชาการ'],
  //     image: 'https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=800'
  //   },
  //   {
  //     id: 2,
  //     title: "แจ้งปิดถนนชั่วคราวเพื่อซ่อมแซมท่อประปาหลัก",
  //     summary: "แจ้งให้ทราบว่าจะมีการปิดถนนสายหลักบริเวณหน้าสำนักงานเขต เพื่อดำเนินการซ่อมแซมท่อประปาหลักที่ชำรุด",
  //     content: `ด้วยการประปานครหลวงจำเป็นต้องดำเนินการซ่อมแซมท่อประปาหลักขนาด 600 มิลลิเมตร ที่เกิดการรั่วซึมบริเวณหน้าสำนักงานเขต\n\nรายละเอียดการปิดถนน:\n- วันที่: 10-12 กันยายน 2567\n- เวลา: 22:00-05:00 น.\n- บริเวณ: ถนนสายหลัก หน้าสำนักงานเขต ระยะทาง 200 เมตร\n\nเส้นทางเลี่ยง:\n- รถยนต์: ใช้เส้นทางถนนข้างเคียง (ถนนสาขา A และ B)\n- รถโดยสารประจำทาง: ปรับเปลี่ยนเส้นทางชั่วคราว\n- รถจักรยานยนต์: สามารถผ่านได้ในช่วงเวลา 05:00-22:00 น.\n\nผลกระทบที่คาดว่าจะเกิดขึ้น:\n- การจราจรติดขัดในช่วงเวลาเร่งด่วน\n- การหยุดจ่ายน้ำประปาในพื้นที่ใกล้เคียง 4-6 ชั่วโมง\n- เสียงดังจากการใช้เครื่องจักรในการซ่อมแซม\n\nขออภัยในความไม่สะดวก และขอความร่วมมือจากประชาชนในการปฏิบัติตามป้ายจราจรและคำแนะนำของเจ้าหน้าที่\n\nสอบถามข้อมูลเพิ่มเติม: 02-123-4567 ต่อ 301`,
  //     category: 'เตือนภัย',
  //     priority: 'สูง',
  //     publishDate: '2024-09-07',
  //     author: 'ฝ่ายโยธา',
  //     views: 5234,
  //     tags: ['ปิดถนน', 'ซ่อมแซม', 'ประปา'],
  //     image: 'https://images.pexels.com/photos/2219024/pexels-photo-2219024.jpeg?auto=compress&cs=tinysrgb&w=800'
  //   },
  //   {
  //     id: 3,
  //     title: "กิจกรรมวันผู้สูงอายุแห่งชาติ ประจำปี 2567",
  //     summary: "เชิญชวนผู้สูงอายุและประชาชนทั่วไปร่วมงานวันผู้สูงอายุแห่งชาติ พร้อมกิจกรรมตรวจสุขภาพฟรีและการแสดงต่างๆ",
  //     content: `สำนักงานเขตขอเชิญชวนผู้สูงอายุและประชาชนทั่วไป ร่วมงานวันผู้สูงอายุแห่งชาติ ประจำปี 2567\n\nรายละเอียดงาน:\n- วันที่: 1 ตุลาคม 2567 (วันอาทิตย์)\n- เวลา: 08:00-16:00 น.\n- สถานที่: ลานอเนกประสงค์ สำนักงานเขต\n- ธีมงาน: "ผู้สูงอายุไทย สุขภาพดี มีคุณค่า"\n\nกิจกรรมภายในงาน:\n- ตรวจสุขภาพฟรี (วัดความดัน ตรวจน้ำตาล ตรวจสายตา)\n- การแสดงดนตรีไทยโดยวงดุริยางค์ผู้สูงอายุ\n- การแสดงแอโรบิคและโยคะสำหรับผู้สูงอายุ\n- นิทรรศการสินค้าและผลิตภัณฑ์จากผู้สูงอายุ\n- การให้คำปรึกษาด้านสุขภาพและสวัสดิการ\n- มุมถ่ายรูปและกิจกรรมสันทนาการ\n\nของที่ระลึกและรางวัล:\n- ของที่ระลึกสำหรับผู้เข้าร่วมงาน 500 ท่านแรก\n- รางวัลจากการจับสลากทุก 30 นาที\n- อาหารและเครื่องดื่มฟรีตลอดงาน\n\nการลงทะเบียน:\n- ลงทะเบียนหน้างาน ตั้งแต่ 07:30 น.\n- หรือลงทะเบียนล่วงหน้าที่สำนักงานเขต\n- โทรศัพท์: 02-123-4567 ต่อ 401\n\nหมายเหตุ: มีรถรับส่งฟรีสำหรับผู้สูงอายุที่ไม่สะดวกเดินทาง`,
  //     category: 'กิจกรรม',
  //     priority: 'กลาง',
  //     publishDate: '2024-09-06',
  //     author: 'ฝ่ายสวัสดิการสังคม',
  //     views: 1876,
  //     tags: ['ผู้สูงอายุ', 'กิจกรรม', 'สุขภาพ'],
  //     image: 'https://images.pexels.com/photos/7551659/pexels-photo-7551659.jpeg?auto=compress&cs=tinysrgb&w=800'
  //   },
  //   {
  //     id: 4,
  //     title: "บริการใหม่: ระบบจองคิวออนไลน์สำหรับงานทะเบียนราษฎร",
  //     summary: "เปิดตัวระบบจองคิวออนไลน์ใหม่ เพื่อลดเวลารอคอยและเพิ่มความสะดวกในการใช้บริการงานทะเบียนราษฎร",
  //     content: `สำนักงานเขตเปิดตัวระบบจองคิวออนไลน์ใหม่ เพื่อให้บริการที่สะดวกและรวดเร็วยิ่งขึ้น\n\nบริการที่สามารถจองคิวออนไลน์:\n- ขอคัดสำเนาทะเบียนบ้าน\n- ขอคัดสำเนาบัตรประจำตัวประชาชน\n- แจ้งย้ายที่อยู่\n- แจ้งเกิด แจ้งตาย แจ้งการสมรส\n- ขอใบรับรองต่างๆ\n- บริการรับรองเอกสาร\n\nวิธีการจองคิวออนไลน์:\n1. เข้าเว็บไซต์ www.districtoffice.go.th\n2. เลือกเมนู "จองคิวออนไลน์"\n3. เลือกประเภทบริการที่ต้องการ\n4. กรอกข้อมูลส่วนตัวและเลือกวันเวลา\n5. ยืนยันการจองและรับหมายเลขคิว\n6. มาใช้บริการตามวันเวลาที่จอง\n\nข้อดีของระบบใหม่:\n- ประหยัดเวลารอคอย\n- สามารถเลือกวันเวลาที่สะดวก\n- ลดความแออัดในสำนักงาน\n- ติดตามสถานะการให้บริการได้\n- รับการแจ้งเตือนผ่าน SMS\n\nเวลาให้บริการ:\n- วันจันทร์-ศุกร์: 08:30-16:30 น.\n- พักเที่ยง: 12:00-13:00 น.\n- หยุดเสาร์-อาทิตย์ และวันหยุดนักขัตฤกษ์\n\nหมายเหตุ: ยังคงให้บริการแบบ Walk-in สำหรับผู้ที่ไม่สะดวกจองออนไลน์`,
  //     category: 'บริการ',
  //     priority: 'กลาง',
  //     publishDate: '2024-09-05',
  //     author: 'ฝ่ายทะเบียนราษฎร',
  //     views: 3421,
  //     tags: ['จองคิว', 'ออนไลน์', 'ทะเบียนราษฎร'],
  //     image: 'https://images.pexels.com/photos/5668473/pexels-photo-5668473.jpeg?auto=compress&cs=tinysrgb&w=800'
  //   },
  //   {
  //     id: 5,
  //     title: "ประชุมประชาคมเพื่อรับฟังความคิดเห็นโครงการปรับปรุงสวนสาธารณะ",
  //     summary: "เชิญประชาชนร่วมประชุมประชาคมเพื่อรับฟังความคิดเห็นและข้อเสนอแนะในการปรับปรุงสวนสาธารณะในเขต",
  //     content: `สำนักงานเขตขอเชิญประชาชนทุกท่านร่วมประชุมประชาคม เพื่อรับฟังความคิดเห็นเกี่ยวกับโครงการปรับปรุงสวนสาธารณะ\n\nรายละเอียดการประชุม:\n- วันที่: 20 กันยายน 2567 (วันพฤหัสบดี)\n- เวลา: 18:30-20:30 น.\n- สถานที่: ห้องประชุมใหญ่ ชั้น 3 สำนักงานเขต\n- ผู้เข้าร่วม: ประชาชนในเขต ผู้นำชุมชน และหน่วยงานที่เกี่ยวข้อง\n\nวัตถุประสงค์:\n- นำเสนอแผนการปรับปรุงสวนสาธารณะ\n- รับฟังความคิดเห็นและข้อเสนอแนะจากประชาชน\n- ร่วมกันวางแผนการพัฒนาที่เหมาะสม\n- สร้างความเข้าใจและการมีส่วนร่วม\n\nประเด็นที่จะหารือ:\n- การปรับปรุงภูมิทัศน์และการจัดสวน\n- เพิ่มเติมอุปกรณ์ออกกำลังกาย\n- ปรับปรุงลานกิจกรรมและที่นั่งพักผ่อน\n- ระบบแสงสว่างและความปลอดภัย\n- การจัดการขยะและสิ่งแวดล้อม\n- งบประมาณและระยะเวลาดำเนินการ\n\nสิ่งที่ควรเตรียม:\n- ความคิดเห็นและข้อเสนอแนะ\n- รูปภาพหรือตัวอย่างที่ต้องการให้ปรับปรุง\n- ปัญหาที่พบในปัจจุบัน\n\nของว่างและเครื่องดื่มจัดให้ตลอดการประชุม\nลงทะเบียนล่วงหน้า: 02-123-4567 ต่อ 501`,
  //     category: 'ข่าวสาร',
  //     priority: 'กลาง',
  //     publishDate: '2024-09-04',
  //     author: 'ฝ่ายพัฒนาชุมชน',
  //     views: 987,
  //     tags: ['ประชาคม', 'สวนสาธารณะ', 'ปรับปรุง'],
  //     image: 'https://images.pexels.com/photos/1105766/pexels-photo-1105766.jpeg?auto=compress&cs=tinysrgb&w=800'
  //   },
  //   {
  //     id: 6,
  //     title: "เตือนภัยฝนตกหนักและน้ำท่วมฉับพลัน",
  //     summary: "กรมอุตุนิยมวิทยาพยากรณ์ฝนตกหนักในพื้นที่ ขอให้ประชาชนเตรียมความพร้อมและระวังน้ำท่วมฉับพลัน",
  //     content: `กรมอุตุนิยมวิทยาออกประกาศเตือนภัยฝนตกหนักและลมแรงในพื้นที่เขต ขอให้ประชาชนเตรียมความพร้อมรับมือ\n\nพยากรณ์อากาศ:\n- ระยะเวลา: 12-15 กันยายน 2567\n- ปริมาณฝน: 80-120 มิลลิเมตร\n- ลมแรง: ความเร็ว 40-60 กิโลเมตรต่อชั่วโมง\n- ความเสี่ยง: น้ำท่วมฉับพลัน น้ำป่าไหลหลาก\n\nพื้นที่เสี่ยง:\n- บริเวณลุ่มต่ำและใกล้แหล่งน้ำ\n- ถนนสายหลักที่มีการระบายน้ำไม่ดี\n- อาคารเก่าและป้ายโฆษณาขนาดใหญ่\n- พื้นที่ก่อสร้างและดินถล่ม\n\nมาตรการเตรียมความพร้อม:\n- เตรียมอาหาร น้ำดื่ม และยาประจำตัว\n- ตรวจสอบระบบระบายน้ำรอบบ้าน\n- เก็บของใช้สำคัญไว้ในที่สูง\n- เตรียมไฟฉายและแบตเตอรี่สำรอง\n- ติดตามข่าวสารอย่างใกล้ชิด\n\nเมื่อเกิดเหตุฉุกเฉิน:\n- โทรแจ้งศูนย์ควบคุมเขต: 02-123-4567\n- หมายเลขฉุกเฉิน 24 ชั่วโมง: 191\n- ศูนย์ป้องกันภัยพิบัติ: 1784\n- กู้ภัยการแพทย์: 1669\n\nจุดอพยพฉุกเฉิน:\n- โรงเรียนประชาบำรุง (รองรับ 200 คน)\n- วัดสันติธรรม (รองรับ 150 คน)\n- ศูนย์ชุมชนกลาง (รองรับ 100 คน)\n\nขอความร่วมมือประชาชนหลีกเลี่ยงการเดินทางที่ไม่จำเป็น`,
  //     category: 'เตือนภัย',
  //     priority: 'สูง',
  //     publishDate: '2024-09-03',
  //     author: 'ศูนย์ป้องกันภัยพิบัติ',
  //     views: 6789,
  //     tags: ['เตือนภัย', 'ฝนตก', 'น้ำท่วม'],
  //     image: 'https://images.pexels.com/photos/1118873/pexels-photo-1118873.jpeg?auto=compress&cs=tinysrgb&w=800'
  //   }
  // ];

  // State for media contents (สื่อประชาสัมพันธ์)
  const [mediaContents, setMediaContents] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [mediaError, setMediaError] = useState(null);

  // 2. สร้างคอมโพเนนต์ย่อยสำหรับการ์ดสื่อประชาสัมพันธ์
  function MediaCard({ item }) {
    const formatDate = (dateString) => {
      if (!dateString) return '';
      try {
        return new Date(dateString).toLocaleDateString('th-TH', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (e) {
        return '';
      }
    };

    // ตรวจสอบว่า item มี id และ type หรือไม่
    // ถ้า type เป็น 'news' และมี id ให้ link ไปที่หน้าแสดงข่าวรายละเอียด
    // ถ้า type เป็น 'media' และมี id ให้ link ไปที่หน้าแสดงสื่อรายละเอียด
    // ตรวจสอบว่า id ไม่ใช่ undefined, null, หรือ empty string
    const hasId = item?.id !== undefined && item?.id !== null && item?.id !== '';
    const isValidType = item?.type === 'news' || item?.type === 'media';
    const hasValidLink = hasId && isValidType;
    const linkTo = hasValidLink 
      ? (item?.type === 'news' ? `/news/${item.id}` : `/media/${item.originalId || item.id}`)
      : '#';
    
    // Debug: ตรวจสอบค่า item (แสดงใน console เพื่อ debug) - แสดงผลเสมอ
    console.log('MediaCard render:', { 
      id: item?.id, 
      idType: typeof item?.id,
      type: item?.type,
      hasId,
      hasValidLink,
      linkTo,
      title: item?.title,
      fullItem: item
    });
    
    if ((item?.type === 'news' || item?.type === 'media') && !hasId) {
      console.warn('MediaCard: Item missing or invalid id', { 
        item, 
        id: item?.id, 
        type: item?.type,
        hasId,
        idType: typeof item?.id
      });
    }
    if (hasValidLink) {
      console.log('MediaCard: Valid link', { id: item.id, type: item.type, linkTo });
    } else {
      console.log('MediaCard: Invalid link, will show alert', {
        id: item?.id,
        type: item?.type,
        hasId,
        isValidType,
        hasValidLink,
        linkTo
      });
    }
    
    const handleClick = (e) => {
      console.log('=== MediaCard handleClick ===');
      console.log('hasValidLink:', hasValidLink);
      console.log('linkTo:', linkTo);
      console.log('item:', item);
      console.log('item.id:', item?.id);
      console.log('item.type:', item?.type);
      
      // ถ้าไม่มี valid link ให้แสดงข้อมูล
      if (!hasValidLink || linkTo === '#') {
        console.warn('No valid link, showing alert');
        e.preventDefault();
        // แสดงข้อมูลเบื้องต้น
        if (item?.description) {
          alert(`ชื่อ: ${item.title}\n\nรายละเอียด: ${item.description.slice(0, 200)}${item.description.length > 200 ? '...' : ''}`);
        } else {
          alert(`ชื่อ: ${item.title || 'ไม่มีชื่อ'}`);
        }
      } else {
        console.log('Valid link, should navigate to:', linkTo);
      }
      // ถ้ามี valid link ให้ Link ทำงานตามปกติ (ไม่ preventDefault)
    };

    // Card content component
    const CardContent = (
      <>
        {item.image ? (
          <div className="aspect-w-16 aspect-h-9 w-full bg-muted overflow-hidden">
            <img
              src={item.image}
              alt={item.title}
              className="object-cover w-full h-48 hover:scale-105 transition-transform duration-300"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="w-full h-48 bg-muted flex items-center justify-center hidden">
              <Icon name="Image" size={48} className="text-muted-foreground" />
            </div>
          </div>
        ) : (
          <div className="w-full h-48 bg-muted flex items-center justify-center">
            <Icon name="Image" size={48} className="text-muted-foreground" />
          </div>
        )}
        <div className="p-4 flex-1 flex flex-col justify-between">
          <h3 className="text-base font-semibold text-foreground mb-2 line-clamp-2 hover:text-primary transition-colors">
            {item.title}
          </h3>
          {item.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {item.description}
            </p>
          )}
          <div className="text-xs text-muted-foreground flex items-center gap-2 mt-auto">
            <Icon name="Calendar" size={14} />
            <span>{formatDate(item.date)}</span>
            {item.category && (
              <>
                <span className="mx-1">•</span>
                <span>{item.category}</span>
              </>
            )}
          </div>
        </div>
      </>
    );

    // ถ้ามี valid link ให้ใช้ Link โดยไม่ต้องมี onClick handler
    if (hasValidLink && linkTo !== '#') {
      console.log('=== Rendering MediaCard with Link ===', {
        linkTo,
        itemId: item?.id,
        itemType: item?.type
      });
      return (
        <Link 
          to={linkTo}
          className="bg-card border border-border rounded-lg shadow-government hover:shadow-government-lg transition-government overflow-hidden flex flex-col block"
        >
          {CardContent}
        </Link>
      );
    }
    
    // ถ้าไม่มี valid link ให้ใช้ div พร้อม onClick
    console.log('=== Rendering MediaCard with div (no valid link) ===', {
      hasValidLink,
      linkTo,
      itemId: item?.id,
      itemType: item?.type
    });
    return (
      <div 
        onClick={handleClick}
        className="bg-card border border-border rounded-lg shadow-government hover:shadow-government-lg transition-government overflow-hidden flex flex-col block cursor-pointer"
      >
        {CardContent}
      </div>
    );
  }

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Filter procurement items
  const filteredProcurements = useMemo(() => {
    let items = [...(procurements || [])];

    // Map incoming category (thai/english) to three groups
    const belongsTo = (item, key) => {
      const cat = (item?.category || item?.type || '').toString();
      if (key === 'procure') {
        return /จัดซื้อ|ประกวดราคา|จ้าง|e-bidding|สอบราคา|tor/i.test(cat);
      }
      if (key === 'plan') {
        return /แผน|procurement plan|annual plan/i.test(cat);
      }
      if (key === 'contract') {
        return /สัญญา|ข้อตกลง|contract|ผลการจัดซื้อจัดจ้าง/i.test(cat);
      }
      return true;
    };

    if (procCategory) {
      items = items.filter((it) => belongsTo(it, procCategory));
    }

    if (procSearch) {
      const q = procSearch.toLowerCase();
      items = items.filter(it => (
        it?.title?.toLowerCase()?.includes(q) ||
        it?.subject?.toLowerCase()?.includes(q) ||
        it?.summary?.toLowerCase()?.includes(q)
      ));
    }

    if (procFrom) {
      items = items.filter(it => it?.publish_date && new Date(it.publish_date) >= new Date(procFrom));
    }
    if (procTo) {
      items = items.filter(it => it?.publish_date && new Date(it.publish_date) <= new Date(procTo));
    }

    return items;
  }, [procurements, procCategory, procSearch, procFrom, procTo]);

  // Tabs that map to article categories
  const categoryTabs = new Set(['ข่าวสาร', 'ประกาศ', 'กิจกรรม', 'บริการ', 'เตือนภัย', 'ด่วน']);

  // 3. ดึงข้อมูลข่าวและหนังสือราชการจาก Supabase เมื่อโหลดหน้า
  useEffect(() => {
    async function fetchData() {
      // ดึงข้อมูลข่าว
      setNewsLoading(true);
      setNewsError(null);
      const { data: newsData, error: newsError } = await supabase
        .from('news')
        .select('*')
        .order('publish_date', { ascending: false });
      if (newsError) setNewsError(newsError.message);
      setNews(newsData || []);
      setNewsLoading(false);

      // ดึงข้อมูลสื่อประชาสัมพันธ์
      setMediaLoading(true);
      setMediaError(null);
      try {
        let mediaData = null;
        let mediaError = null;
        
        // ลองดึงจากตาราง media_library ก่อน (ถ้ามี)
        const { data: mediaLibraryData, error: mediaLibraryError } = await supabase
          .from('media_library')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(50);
        
        // ตรวจสอบว่า error เป็นเพราะตารางไม่พบหรือไม่ (เป็นเรื่องปกติ)
        const isTableNotFound = (error) => {
          return error && (
            error.code === 'PGRST116' || 
            error.code === 'PGRST205' || 
            error.message?.includes('Could not find the table') ||
            error.message?.includes('relation') && error.message?.includes('does not exist')
          );
        };
        
        if (!mediaLibraryError && mediaLibraryData && mediaLibraryData.length > 0) {
          // ถ้ามีตาราง media_library และมีข้อมูล ใช้ข้อมูลจากตาราง media_library
          mediaData = mediaLibraryData;
        } else if (mediaLibraryError && !isTableNotFound(mediaLibraryError)) {
          // มี error แต่ไม่ใช่ table not found
          throw mediaLibraryError;
        } else {
          // ลองดึงจากตาราง media (ถ้ามี)
          const { data: mediaTableData, error: mediaTableError } = await supabase
            .from('media')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(50);
          
          if (!mediaTableError && mediaTableData && mediaTableData.length > 0) {
            mediaData = mediaTableData;
          } else if (mediaTableError && !isTableNotFound(mediaTableError)) {
            // มี error แต่ไม่ใช่ table not found
            throw mediaTableError;
          }
          // ถ้า table not found ไม่ต้องทำอะไร ไปใช้ fallback จากตาราง news
        }
        
        if (mediaData && mediaData.length > 0) {
          // ถ้ามีข้อมูลจากตาราง media หรือ media_library
          // แยกเป็น 2 ประเภท:
          // 1. media items ที่มี news_id -> link ไปที่ news detail (type: 'news')
          // 2. media items ที่ไม่มี news_id -> link ไปที่ media detail (type: 'media')
          const mappedMedia = mediaData.map(item => {
            const newsId = item.news_id || item.related_news_id || item.news_item_id;
            const hasNewsId = newsId !== undefined && newsId !== null;
            
            const mappedItem = {
              id: hasNewsId ? newsId : item.id, // ถ้ามี news_id ใช้ news_id, ถ้าไม่มีใช้ item.id
              title: item.title || item.name || item.filename || 'ไม่มีชื่อ',
              description: item.description || item.summary || item.caption || '',
              image: item.image_url || item.thumbnail_url || item.url || item.file_url || '',
              file_url: item.file_url || item.url || '',
              category: item.category || item.type || 'สื่อประชาสัมพันธ์',
              date: item.created_at || item.uploaded_at || item.publish_date || new Date().toISOString(),
              type: hasNewsId ? 'news' : 'media', // ถ้ามี news_id ให้ type เป็น 'news', ถ้าไม่มีให้เป็น 'media'
              originalId: item.id // เก็บ original id ไว้สำหรับ media detail
            };
            
            // Debug: แสดงข้อมูลที่ map แล้ว
            console.log('Mapped media item from media_library/media:', {
              originalItem: item,
              mappedItem: mappedItem,
              newsId: newsId,
              hasNewsId: hasNewsId,
              hasId: !!mappedItem.id,
              type: mappedItem.type
            });
            
            return mappedItem;
          });
          
          // กรองเฉพาะ media items (type: 'media') เท่านั้น ไม่รวม news items
          // เพราะ news items ควรแสดงในแท็บ "ข่าวสารประชาสัมพันธ์" เท่านั้น
          const mediaOnly = mappedMedia.filter(item => item.type === 'media');
          
          console.log('=== Setting mediaContents (from media_library/media table - media only) ===');
          console.log('Total items:', mediaOnly.length);
          console.log('Items with type "media":', mediaOnly.filter(item => item.type === 'media'));
          
          setMediaContents(mediaOnly);
        } else {
          // ถ้าไม่มีข้อมูลจาก media_library/media table ให้แสดงเป็น array ว่าง
          // ไม่ใช้ข้อมูลจาก news table เพราะข่าวควรแสดงในแท็บ "ข่าวสารประชาสัมพันธ์" เท่านั้น
          console.log('No media data found, setting empty array');
          setMediaContents([]);
          setMediaError(null); // ไม่แสดง error เพราะเป็นเรื่องปกติที่อาจจะยังไม่มีข้อมูล
        }
      } catch (e) {
        // ตรวจสอบว่า error เป็นเพราะตารางไม่พบหรือไม่
        if (e.code === 'PGRST116' || e.code === 'PGRST205' || e.message?.includes('Could not find the table')) {
          // ถ้าเป็น error เพราะตารางไม่พบ (เป็นเรื่องปกติ) ให้แสดง array ว่าง
          // ไม่ใช้ข้อมูลจาก news table เพราะข่าวควรแสดงในแท็บ "ข่าวสารประชาสัมพันธ์" เท่านั้น
          console.log('Media table not found, setting empty array');
          setMediaContents([]);
          setMediaError(null); // ไม่แสดง error เพราะเป็นเรื่องปกติที่อาจจะยังไม่มีตาราง
        } else {
          // Error อื่นๆ (ไม่ใช่ table not found)
          console.error('Error fetching media:', e);
          setMediaError(e.message);
          setMediaContents([]);
        }
      } finally {
        setMediaLoading(false);
      }

      // ดึงข้อมูลหนังสือราชการ
      setDocsLoading(true);
      setDocsError(null);
      const { data: docsData, error: docsError } = await supabase
        .from('official_documents')
        .select('*')
        .order('date', { ascending: false });
      if (docsError) setDocsError(docsError.message);
      setOfficialDocs(docsData || []);
      setDocsLoading(false);

      // ดึงข้อมูลจัดซื้อจัดจ้าง (ignore errors if table not found)
      setProcLoading(true);
      setProcError(null);
      try {
        const { data: procData, error: pErr } = await supabase
          .from('procurements')
          .select('*')
          .order('publish_date', { ascending: false });
        if (pErr) throw pErr;
        setProcurements(procData || []);
      } catch (e) {
        // Fallback: ใช้ตาราง news ที่มีหมวดเกี่ยวกับจัดซื้อจัดจ้างแทนชั่วคราว
        try {
          const { data: newsAsProc, error: npErr } = await supabase
            .from('news')
            .select('*')
            .order('publish_date', { ascending: false });
          if (npErr) throw npErr;
          const mapped = (newsAsProc || []).filter(n => {
            const cat = (n?.category || '').toString();
            return /จัดซื้อ|ประกวดราคา|จ้าง|สัญญา|e-bidding|สอบราคา|tor|แผน/i.test(cat);
          }).map(n => ({
            id: n.id,
            title: n.title,
            subject: n.title,
            summary: n.summary,
            publish_date: n.publish_date,
            category: n.category,
            file_url: n.file_url || n.attachment_url || null,
            method: n.method || null,
            budget: n.budget || null,
            type: n.category,
          }));
          setProcurements(mapped);
          setProcError('');
        } catch (fallbackErr) {
          setProcurements([]);
          setProcError(e?.message || fallbackErr?.message || '');
        }
      } finally {
        setProcLoading(false);
      }
    }
    fetchData();
  }, []);

  // 4. ฟิลเตอร์ข่าวตาม search/filters (เหมือนเดิมแต่ใช้ news แทน mockArticles)
  const filteredArticles = useMemo(() => {
    let filtered = [...news];
    
    // กรองตามหมวดหมู่ที่คลิกจากปุ่มสถิติ
    if (filters?.category && filters?.category !== 'all') {
      if (filters?.category === 'ข่าวสาร') {
        // ปุ่ม "ข่าวสาร" ให้รวม "ข่าวประชาสัมพันธ์" ด้วย
        filtered = filtered?.filter(article => 
          article?.category === 'ข่าวสาร' || 
          article?.category === 'ข่าวประชาสัมพันธ์' ||
          article?.category === 'Public Relations News' ||
          article?.category === 'News'
        );
      } else if (filters?.category === 'ประกาศ') {
        // ปุ่ม "ประกาศ" แสดงเฉพาะข่าวที่มีหมวดหมู่ "ประกาศ" ในตาราง news
        filtered = filtered?.filter(article => article?.category === 'ประกาศ');
      } else {
        filtered = filtered?.filter(article => article?.category === filters?.category);
      }
    }
    
    if (activeTab !== 'all' && categoryTabs.has(activeTab)) {
      filtered = filtered?.filter(article => article?.category === activeTab);
    }
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(article =>
        article?.title?.toLowerCase()?.includes(query) ||
        article?.summary?.toLowerCase()?.includes(query) ||
        article?.content?.toLowerCase()?.includes(query) ||
        (article?.tags && article?.tags?.some(tag => tag?.toLowerCase()?.includes(query)))
      );
    }
    if (filters?.priority !== 'all') {
      filtered = filtered?.filter(article => article?.priority === filters?.priority);
    }
    filtered?.sort((a, b) => {
      switch (filters?.sortBy) {
        case 'oldest':
          return new Date(a.publish_date) - new Date(b.publish_date);
        case 'priority':
          const priorityOrder = { 'สูง': 3, 'กลาง': 2, 'ปกติ': 1 };
          return priorityOrder?.[b?.priority] - priorityOrder?.[a?.priority];
        case 'views':
          return b?.views - a?.views;
        case 'newest':
        default:
          return new Date(b.publish_date) - new Date(a.publish_date);
      }
    });
    return filtered;
  }, [searchQuery, filters, activeTab, news]);

  const handleExpandArticle = (articleId) => {
    setExpandedArticles(prev =>
      prev?.includes(articleId)
        ? prev?.filter(id => id !== articleId)
        : [...prev, articleId]
    );
  };

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const tabs = [
    { key: 'media', label: 'สื่อประชาสัมพันธ์', icon: 'Megaphone' },
    { key: 'pr', label: 'ข่าวสารประชาสัมพันธ์', icon: 'Info' },
    { key: 'official-docs', label: 'หนังสือราชการ', icon: 'FileText' },
    { key: 'procurement', label: 'จัดซื้อจัดจ้าง', icon: 'ShoppingCart' }
  ];

  const officialDocItems = [
    { key: 'orders', label: 'หนังสือสั่งการ', icon: 'BookMarked' },
    { key: 'announces', label: 'ประกาศ', icon: 'Megaphone' },
    { key: 'web-issues', label: 'เรื่องที่เรียนรายทางเว็บไซต์', icon: 'Globe' },
    { key: 'meetings', label: 'งานประชุม', icon: 'Users' },
    { key: 'meeting-orders', label: 'ข้อสั่งการที่ประชุม ปค.', icon: 'ClipboardList' }
  ];

  // PDF list (mock). Replace url with actual PDF paths under public/assets/documents
  const pdfDocs = [
    { id: 'd1', cat: 'orders', title: 'มท 0301.1/ว 2766 การพิจารณาเลื่อนเงินเดือน...', date: '2025-08-22', tag: 'ปกติ', url: '/assets/documents/sample1.pdf', views: 250 },
    { id: 'd2', cat: 'orders', title: 'มท 0301.1/ว 29232 การปรับตำแหน่งประเภทอำนวยการ...', date: '2025-08-22', tag: 'ปกติ', url: '/assets/documents/sample2.pdf', views: 180 },
    { id: 'd3', cat: 'announces', title: 'ประกาศ เรื่อง แนวทางการดำเนินงาน...', date: '2025-08-18', tag: 'ด่วนที่สุด', url: '/assets/documents/sample3.pdf', views: 96 },
    { id: 'd4', cat: 'meetings', title: 'ระเบียบวาระการประชุม ครั้งที่ 3/2568', date: '2025-08-10', tag: 'ปกติ', url: '/assets/documents/sample4.pdf', views: 72 },
    { id: 'd5', cat: 'meeting-orders', title: 'ข้อสั่งการที่ประชุม ปค. เดือนสิงหาคม', date: '2025-08-05', tag: 'ปกติ', url: '/assets/documents/sample5.pdf', views: 54 }
  ];

  const filteredPdfDocs = useMemo(() => {
    let filtered = officialDocs;
    
    // Filter by category
    if (officialCategory !== 'all') {
      // แปลง category key เป็น category name
      const categoryMap = {
        'orders': 'คำสั่ง',
        'announces': ['ประกาศ', 'คำสั่ง', 'ระเบียบ', 'หนังสือเวียน', 'หนังสือแจ้ง'], // แสดงหนังสือราชการทั้งหมด
        'web-issues': 'เรื่องที่เรียนรายทางเว็บไซต์',
        'meetings': 'งานประชุม',
        'meeting-orders': 'ข้อสั่งการที่ประชุม ปค.'
      };
      const categoryValue = categoryMap[officialCategory];
      if (categoryValue) {
        if (Array.isArray(categoryValue)) {
          // ถ้าเป็น array ให้แสดงหมวดหมู่ทั้งหมด
          filtered = filtered.filter(doc => categoryValue.includes(doc.category));
        } else {
          // ถ้าเป็น string ให้แสดงหมวดหมู่เดียว
          filtered = filtered.filter(doc => doc.category === categoryValue);
        }
      }
    }
    
    // Filter by search
    if (officialSearch) {
      const query = officialSearch.toLowerCase();
      filtered = filtered.filter(doc => 
        doc.title.toLowerCase().includes(query) ||
        doc.document_number.toLowerCase().includes(query) ||
        doc.description.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [officialDocs, officialCategory, officialSearch]);

  // Reset sub-view when switching tabs
  useEffect(() => {
    if (activeTab === 'official-docs') {
      setOfficialMode('landing');
      setOfficialCategory('orders');
      setOfficialSearch('');
      setSelectedDoc(null);
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>ข่าวสารและประชาสัมพันธ์ - อำเภอเทิง</title>
        <meta name="description" content="ติดตามข่าวสารและประกาศสำคัญจากอำเภอเทิง รวมถึงกิจกรรม บริการใหม่ และการแจ้งเตือนต่างๆ" />
        <meta name="keywords" content="ข่าวสาร, ประกาศ, กิจกรรม, อำเภอเทิง, ราชการ" />
      </Helmet>
      <Header />
      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        <BreadcrumbTrail />
        
        {/* Top Tabs - segmented buttons */}
        <div className="mt-2 mb-4 overflow-x-auto">
          <div className="flex items-center gap-3 min-w-full">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center space-x-2 px-4 py-2 whitespace-nowrap rounded-md border transition-government shadow-sm hover:shadow ${activeTab === tab.key ? 'bg-primary/10 border-primary text-foreground' : 'bg-card border-border text-muted-foreground hover:text-foreground'}`}
                aria-pressed={activeTab === tab.key}
              >
                <Icon name={tab.icon} size={16} />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Official Documents Views */}
        {activeTab === 'official-docs' ? (
          officialMode === 'landing' ? (
            <div className="mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                {officialDocItems.map(item => (
                  <button
                    key={item.key}
                    onClick={() => { setOfficialCategory(item.key); setOfficialMode('list'); }}
                    className="group"
                  >
                    <div className="bg-card border border-border rounded-lg p-6 shadow-government hover:shadow-government-lg transition-government h-full flex flex-col items-center justify-center text-center">
                      <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4 group-hover:bg-primary/10">
                        <Icon name={item.icon} size={32} className="text-primary" />
                      </div>
                      <div className="text-foreground font-semibold">{item.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : officialMode === 'list' ? (
            <div className="mt-2 grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-card border border-border rounded-lg p-4 shadow-government">
                  <div className="text-sm font-semibold mb-3">หนังสือราชการ</div>
                  <div className="space-y-2">
                    {officialDocItems.map(item => (
                      <button
                        key={item.key}
                        onClick={() => setOfficialCategory(item.key)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-md border ${officialCategory === item.key ? 'bg-primary/10 border-primary text-foreground' : 'bg-muted/30 border-border text-muted-foreground hover:text-foreground'}`}
                      >
                        <span className="flex items-center space-x-2">
                          <Icon name={item.icon} size={16} />
                          <span className="text-sm">{item.label}</span>
                        </span>
                        {officialCategory === item.key && <Icon name="ChevronRight" size={16} />}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Content list */}
              <div className="lg:col-span-3">
                <div className="bg-card border border-border rounded-lg p-4 shadow-government mb-4">
                  <div className="flex flex-col md:flex-row gap-3 items-center">
                    <input
                      type="text"
                      placeholder="ค้นหา..."
                      value={officialSearch}
                      onChange={(e) => setOfficialSearch(e?.target?.value)}
                      className="w-full md:flex-1 bg-background border border-border rounded-md px-3 py-2 text-foreground"
                    />
                    <button
                      onClick={() => setOfficialMode('landing')}
                      className="px-4 py-2 rounded-md border border-border text-sm text-muted-foreground hover:text-foreground"
                    >
                      กลับหน้าหมวดหมู่
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {filteredPdfDocs.map(doc => (
                    <button
                      key={doc.id}
                      onClick={() => { setSelectedDoc(doc); setOfficialMode('detail'); }}
                      className="w-full text-left bg-card border border-border rounded-lg p-4 shadow-government hover:shadow-government-lg transition-government"
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          <Icon name="FileText" size={18} className="text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="text-foreground font-medium leading-snug">{doc.title}</div>
                          <div className="mt-2 text-xs text-muted-foreground flex items-center gap-3">
                            <span className="flex items-center gap-1"><Icon name="Calendar" size={12} /> {new Date(doc.date).toLocaleDateString('th-TH')}</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted/40 text-muted-foreground">{doc.category}</span>
                            {doc.document_number && (
                              <span className="flex items-center gap-1"><Icon name="FileText" size={12} /> {doc.document_number}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.file_url && (
                            <a
                              href={doc.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="p-2 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                              title="ดาวน์โหลดไฟล์"
                            >
                              <Icon name="Download" size={16} />
                            </a>
                          )}
                          <div className="hidden sm:block">
                            <Icon name="ChevronRight" size={18} className="text-muted-foreground" />
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}

                  {filteredPdfDocs.length === 0 && (
                    <div className="text-center text-muted-foreground py-8 border border-dashed border-border rounded-lg">
                      ไม่พบเอกสารในหมวดนี้
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            // Detail view
            <div className="mt-2 grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-card border border-border rounded-lg p-4 shadow-government">
                  <div className="text-sm font-semibold mb-3">หนังสือราชการ</div>
                  <div className="space-y-2">
                    {officialDocItems.map(item => (
                      <button
                        key={item.key}
                        onClick={() => { setOfficialCategory(item.key); setOfficialMode('list'); }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-md border ${officialCategory === item.key ? 'bg-primary/10 border-primary text-foreground' : 'bg-muted/30 border-border text-muted-foreground hover:text-foreground'}`}
                      >
                        <span className="flex items-center space-x-2">
                          <Icon name={item.icon} size={16} />
                          <span className="text-sm">{item.label}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Detail content */}
              <div className="lg:col-span-3">
                {selectedDoc && (
                  <div className="space-y-4">
                    <div className="bg-card border border-border rounded-lg p-6 shadow-government">
                      <h1 className="text-xl lg:text-2xl font-bold text-foreground leading-snug">{selectedDoc.title}</h1>
                      <div className="mt-2 text-xs text-muted-foreground flex items-center gap-4">
                        <span className="flex items-center gap-1"><Icon name="Calendar" size={12} /> {new Date(selectedDoc.date).toLocaleDateString('th-TH')}</span>
                        <span className="flex items-center gap-1"><Icon name="Eye" size={12} /> เข้าชม {selectedDoc.views} ครั้ง</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted/40 text-muted-foreground">{selectedDoc.tag}</span>
                      </div>
                    </div>

                    {selectedDoc.file_url ? (
                      <div className="bg-card border border-border rounded-lg p-6 shadow-government">
                        <a
                          href={selectedDoc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between bg-muted/20 hover:bg-muted/30 transition-government rounded-md p-4"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded bg-red-100 text-red-600 flex items-center justify-center">
                              <Icon name="FileText" size={18} />
                            </div>
                            <div className="text-foreground">เปิดไฟล์ PDF</div>
                          </div>
                          <div className="text-sm text-muted-foreground flex items-center gap-1">
                            <Icon name="Download" size={16} /> ดาวน์โหลด
                          </div>
                        </a>
                      </div>
                    ) : (
                      <div className="bg-card border border-border rounded-lg p-6 shadow-government">
                        <div className="text-center text-muted-foreground py-4">
                          <Icon name="FileText" size={32} className="mx-auto mb-2" />
                          <p>ไม่มีไฟล์แนบ</p>
                        </div>
                      </div>
                    )}

                    <div>
                      <button
                        onClick={() => setOfficialMode('list')}
                        className="px-4 py-2 rounded-md border border-border text-sm text-muted-foreground hover:text-foreground"
                      >
                        กลับไปยังรายการ
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )
        ) : activeTab === 'media' ? (
          <div className="mt-4">
            {mediaLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {[...Array(8)].map((_, index) => (
                  <div key={index} className="bg-card border border-border rounded-lg shadow-government animate-pulse">
                    <div className="aspect-w-16 aspect-h-9 w-full bg-muted h-48" />
                    <div className="p-4">
                      <div className="h-4 bg-muted rounded mb-2" />
                      <div className="h-3 bg-muted rounded w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : mediaError ? (
              <div className="text-center py-8 text-red-600">
                <p>เกิดข้อผิดพลาดในการโหลดข้อมูล: {mediaError}</p>
              </div>
            ) : mediaContents.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Image" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground text-lg">ยังไม่มีสื่อประชาสัมพันธ์</p>
                <p className="text-muted-foreground text-sm mt-2">กรุณาเพิ่มข้อมูลในหน้าแอดมิน</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {mediaContents.map(item => (
                  <MediaCard key={item.id} item={item} />
                ))}
              </div>
            )}
          </div>
        ) : activeTab === 'procurement' ? (
          <div className="mt-2 grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg p-4 shadow-government">
                <div className="text-sm font-semibold mb-3">ประชาสัมพันธ์</div>
                <div className="space-y-2">
                  <button
                    onClick={() => setProcCategory('procure')}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md border ${procCategory === 'procure' ? 'bg-primary/10 border-primary text-foreground' : 'bg-muted/30 border-border text-muted-foreground hover:text-foreground'}`}
                  >
                    <span className="flex items-center space-x-2"><Icon name="Megaphone" size={16} /><span className="text-sm">จัดซื้อ-จัดจ้าง</span></span>
                    {procCategory === 'procure' && <Icon name="ChevronRight" size={16} />}
                  </button>
                  <button
                    onClick={() => setProcCategory('plan')}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md border ${procCategory === 'plan' ? 'bg-primary/10 border-primary text-foreground' : 'bg-muted/30 border-border text-muted-foreground hover:text-foreground'}`}
                  >
                    <span className="flex items-center space-x-2"><Icon name="ClipboardList" size={16} /><span className="text-sm">ประกาศแผนจัดซื้อจัดจ้าง</span></span>
                    {procCategory === 'plan' && <Icon name="ChevronRight" size={16} />}
                  </button>
                  <button
                    onClick={() => setProcCategory('contract')}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md border ${procCategory === 'contract' ? 'bg-primary/10 border-primary text-foreground' : 'bg-muted/30 border-border text-muted-foreground hover:text-foreground'}`}
                  >
                    <span className="flex items-center space-x-2"><Icon name="FileText" size={16} /><span className="text-sm">ข้อมูลสาระสำคัญในสัญญา</span></span>
                    {procCategory === 'contract' && <Icon name="ChevronRight" size={16} />}
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-3">
              {procMode === 'list' ? (
                <>
                  <div className="bg-card border border-border rounded-lg p-4 shadow-government mb-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
                      <div className="md:col-span-2">
                        <div className="relative">
                          <input
                            type="search"
                            placeholder="ค้นหา.."
                            value={procSearch}
                            onChange={(e) => setProcSearch(e?.target?.value)}
                            className="w-full bg-background border border-border rounded-md px-3 py-2 pl-9 text-foreground"
                          />
                          <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        </div>
                      </div>
                      <div>
                        <input type="date" value={procFrom} onChange={(e)=>setProcFrom(e?.target?.value)} className="w-full bg-background border border-border rounded-md px-3 py-2 text-foreground" />
                      </div>
                      <div>
                        <input type="date" value={procTo} onChange={(e)=>setProcTo(e?.target?.value)} className="w-full bg-background border border-border rounded-md px-3 py-2 text-foreground" />
                      </div>
                    </div>
                  </div>

                  {procLoading ? (
                    <div className="space-y-4">
                      {[...Array(4)]?.map((_, i) => (
                        <div key={i} className="bg-card border border-border rounded-lg shadow-government h-28 animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredProcurements?.map(item => (
                        <button
                          key={item.id}
                          onClick={() => { setSelectedProc(item); setProcMode('detail'); }}
                          className="w-full text-left bg-card border border-border rounded-lg p-4 shadow-government hover:shadow-government-lg transition-government"
                        >
                          <div className="flex items-start gap-4">
                            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center shrink-0">
                              <div className="w-16 h-16 rounded-full bg-muted/60" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-foreground font-semibold leading-snug line-clamp-2">
                                {item.title || item.subject}
                              </div>
                              {item.summary && (
                                <div className="mt-1 text-sm text-muted-foreground line-clamp-2">{item.summary}</div>
                              )}
                              <div className="mt-2 text-xs text-muted-foreground flex items-center gap-3">
                                {item.publish_date && (
                                  <span className="flex items-center gap-1"><Icon name="Calendar" size={12} /> {new Date(item.publish_date).toLocaleDateString('th-TH')}</span>
                                )}
                                {(item.category || item.type) && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted/40 text-muted-foreground">{item.category || item.type}</span>
                                )}
                              </div>
                              {item.file_url && (
                                <div className="mt-2">
                                  <a href={item.file_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline text-sm">
                                    <Icon name="Download" size={14} /> ดาวน์โหลดไฟล์
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}

                      {filteredProcurements?.length === 0 && (
                        <div className="text-center text-muted-foreground py-8 border border-dashed border-border rounded-lg">
                          ไม่พบรายการในช่วงที่ค้นหา
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <div className="bg-card border border-border rounded-lg p-6 shadow-government">
                    <h1 className="text-xl lg:text-2xl font-bold text-foreground leading-snug">{selectedProc?.title || selectedProc?.subject}</h1>
                    <div className="mt-2 text-xs text-muted-foreground flex items-center gap-4">
                      {selectedProc?.publish_date && (
                        <span className="flex items-center gap-1"><Icon name="Calendar" size={12} /> {new Date(selectedProc?.publish_date).toLocaleDateString('th-TH')}</span>
                      )}
                      {(selectedProc?.category || selectedProc?.type) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted/40 text-muted-foreground">{selectedProc?.category || selectedProc?.type}</span>
                      )}
                    </div>
                  </div>

                  <div className="bg-card border border-border rounded-lg shadow-government overflow-hidden">
                    <div className="px-4 py-3 border-b border-border font-semibold">ไฟล์แนบ</div>
                    <div className="divide-y divide-border">
                      {(() => {
                        // รองรับทั้งรูปแบบ array (files) และเดี่ยว (file_url)
                        const files = Array.isArray(selectedProc?.files) && selectedProc?.files?.length
                          ? selectedProc?.files
                          : (selectedProc?.file_url ? [{ url: selectedProc?.file_url, name: selectedProc?.file_name || (selectedProc?.title || selectedProc?.subject) }] : []);
                        return files.length ? files.map((f, idx) => (
                          <div key={idx} className="flex items-center justify-between px-4 py-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-9 h-9 rounded bg-red-100 text-red-600 flex items-center justify-center shrink-0"><Icon name="FileText" size={16} /></div>
                              <div className="truncate text-sm text-foreground">{f?.name || 'เอกสารแนบ'}</div>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              {selectedProc?.publish_date && <span>{new Date(selectedProc?.publish_date).toLocaleDateString('th-TH')}</span>}
                              <a href={f?.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline"><Icon name="Download" size={14} /> ดาวน์โหลด</a>
                            </div>
                          </div>
                        )) : (
                          <div className="px-4 py-6 text-center text-muted-foreground text-sm">ไม่มีไฟล์แนบ</div>
                        );
                      })()}
                    </div>
                  </div>

                  <div>
                    <button onClick={() => { setProcMode('list'); setSelectedProc(null); }} className="px-4 py-2 rounded-md border border-border text-sm text-muted-foreground hover:text-foreground">
                      กลับไปยังรายการ
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
        <NewsStats
              totalArticles={news?.length}
          filteredCount={filteredArticles?.length}
          filters={filters}
          searchQuery={searchQuery}
          newsData={news}
          onCategoryClick={(category) => {
            setFilters(prev => ({ ...prev, category }));
          }}
        />

        <FilterToolbar
          onFilterChange={setFilters}
          onSearchChange={setSearchQuery}
          searchQuery={searchQuery}
          filters={filters}
        />

        <NewsGrid
          articles={filteredArticles}
          expandedArticles={expandedArticles}
          onExpandArticle={handleExpandArticle}
              loading={newsLoading}
        />

        {filteredArticles?.length > 6 && (
          <div className="fixed bottom-6 right-6 z-50">
            <button
              onClick={handleScrollToTop}
              className="w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-government-lg hover:bg-primary/90 transition-government flex items-center justify-center"
              title="กลับไปด้านบน"
            >
              <Icon name="ArrowUp" size={20} />
            </button>
          </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default NewsAndAnnouncements;